import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useLazyQuery } from '@apollo/client'
import { ActionIcon, Avatar, Menu, TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import Empty from 'src/components/Feedback/Empty/Empty'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_VOICEMAIL_TEMPLATES,
  DELETE_VOICEMAIL_TEMPLATE,
  GET_DOWNLOAD_URL,
} from 'src/graphql/voiceMailTemplate.graphql'
import IconCopy from 'src/icons/IconCopy'
import IconPause from 'src/icons/IconPause'
import IconPlay from 'src/icons/IconPlay'
import IconSearch from 'src/icons/IconSearch'
import IconThreeDots from 'src/icons/IconThreeDots'
import { buildFullName } from 'src/lib/formatters'

import CreateTemplateModal from './components/CreateTemplateModal'

const onSuccess = () => {
  toast('Your text template was deleted successfully', 'success')
}

const VoiceMailTemplateTab = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [editing, setEditing] = useState(false)
  const [createModalOpened, createModalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)
  const defaultResultsPerPage = 20
  const [isPlaying, setIsPlaying] = useState(null)
  const audioRef = useRef(null)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  //QUERIES:
  const {
    data: voiceMailTemplatesList,
    loading: voiceMailTemplatesListLoading,
    refetch,
  } = useQuery(GET_VOICEMAIL_TEMPLATES, {
    variables: {
      pageInfo: {
        limit: defaultResultsPerPage,
        offset: 0,
      },
      orderBy: {
        field: 'title',
        direction: 'asc',
      },
      search: debouncedSearchTerm || undefined,
    },
    onError,
  })

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({
        pageInfo: {
          limit,
          offset,
        },
        orderBy: {
          field: 'title',
          direction: 'asc',
        },
        search: debouncedSearchTerm || undefined,
      })
    },
    [refetch, debouncedSearchTerm]
  )

  const [getDownloadUrl] = useLazyQuery(GET_DOWNLOAD_URL, {
    onCompleted: (data) => {
      audioRef?.current?.pause()
      const audio = new Audio(data.voiceMailDownloadUrl.url)
      audioRef.current = audio
      audioRef.current.play()
      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false)
      })
    },
    onError,
  })

  //MUTATIONS:
  const [deleteTemplate] = useMutation(DELETE_VOICEMAIL_TEMPLATE, {
    refetchQueries: [GET_VOICEMAIL_TEMPLATES],
    onCompleted: () => {
      onSuccess()
    },
    onError,
  })

  //HANDLERS:
  const onEdit = useCallback((element) => {
    setSelectedTemplate(element)
    setEditing(true)
    createModalHandlers.open()
  }, [])

  const onCreate = useCallback(() => {
    setSelectedTemplate(undefined)
    setEditing(false)
    createModalHandlers.open()
  }, [])

  const onDelete = useCallback(() => {
    deleteTemplate({
      variables: {
        voiceMailTemplateId: selectedTemplate.voiceMailTemplateId,
      },
    })
  }, [selectedTemplate])

  const onPlay = useCallback((voiceMailTemplate) => {
    setIsPlaying(voiceMailTemplate.voiceMailTemplateId)
    getDownloadUrl({
      variables: {
        voiceMailTemplateId: voiceMailTemplate.voiceMailTemplateId,
      },
    })
  }, [])

  const onPause = useCallback(() => {
    setIsPlaying(null)
    audioRef?.current?.pause()
  }, [audioRef?.current])

  // This resets the selected template to null when the modal is closed
  //  - Creating a new template sets the selected template to undefined
  //  - Editing a template sets the selected template to the template being edited
  // Note: This distinction is important in order to reset the CreateTemplateModal's form
  useEffect(() => {
    if (!createModalOpened) {
      setSelectedTemplate(null)
    }
  }, [createModalOpened])

  const templates = voiceMailTemplatesList?.voiceMailTemplates?.items ?? []
  const totalCount = voiceMailTemplatesList?.voiceMailTemplates?.totalCount ?? 0

  const columns = useMemo(
    () => [
      {
        key: 'voiceMailTemplateId',
        label: 'ID',
        width: 6,
        render: (template) => (
          <Tooltip label={template.voiceMailTemplateId} withArrow>
            <ActionIcon
              variant="subtle"
              size={14}
              color="gray"
              onClick={(e) => {
                e.stopPropagation()
                navigator?.clipboard?.writeText?.(template.voiceMailTemplateId)
              }}
            >
              <IconCopy className="fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        ),
      },
      {
        key: 'title',
        label: 'Template Name',
        render: (template) => {
          const showPlay =
            !isPlaying || isPlaying !== template.voiceMailTemplateId

          return (
            <div className="flex flex-row items-center gap-1">
              <p>{template.title}</p>
              {showPlay && (
                <ActionIcon
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPlay(template)
                  }}
                >
                  <IconPlay className="h-4 fill-none stroke-doubleNickel-brand-500" />
                </ActionIcon>
              )}
              {isPlaying === template.voiceMailTemplateId && (
                <ActionIcon
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPause()
                  }}
                >
                  <IconPause className="h-4 fill-none stroke-doubleNickel-brand-500" />
                </ActionIcon>
              )}
            </div>
          )
        },
      },
      {
        key: 'createdBy',
        label: 'Created By',
        width: 10,
        render: (template) =>
          template.createdBy ? (
            <Tooltip label={buildFullName(template.createdBy)} withArrow>
              <Avatar color="blue" size="sm" radius="sm">
                {template.createdBy.firstName?.charAt(0)}
                {template.createdBy.lastName?.charAt(0)}
              </Avatar>
            </Tooltip>
          ) : (
            'N/A'
          ),
      },
      {
        key: 'createdAt',
        label: 'Created Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.createdAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.createdAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'updatedAt',
        label: 'Updated Date',
        render: (template) => (
          <Tooltip
            label={dayjs(template.updatedAt).format('MM/DD/YYYY h:mm A')}
            withArrow
          >
            <span>{dayjs(template.updatedAt).format('MM/DD/YYYY')}</span>
          </Tooltip>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 10,
        render: (template) => (
          <div className="flex flex-row justify-end">
            <Menu shadow="md" position="bottom-end">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  className="h-6 w-6"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <IconThreeDots className="fill-doubleNickel-white stroke-doubleNickel-gray-600" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown className="w-40">
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(template)
                  }}
                >
                  Edit
                </Menu.Item>
                <Menu.Item
                  className="text-doubleNickel-gray-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedTemplate(template)
                    deleteModalHandlers.open()
                  }}
                >
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        ),
      },
    ],
    [deleteModalHandlers, isPlaying, onEdit, onPause, onPlay]
  )

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col ">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Voicemail Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {/* These spans are necessary to prevent Google Translate issues  */}
            <span>{totalCount}</span>
            <span> voicemail templates</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <DNButton variant="primary" onClick={() => onCreate()}>
            Create Template
          </DNButton>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={templates}
          enableColumnResize={false}
          enablePagination
          loading={voiceMailTemplatesListLoading}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          onRowClick={(_idx, row) => onEdit(row)}
          defaultResultsPerPage={defaultResultsPerPage}
          emptyStateMessage={
            <Empty title="No templates found" subtitle="Add one to get started" />
          }
        />
      </div>
      <CreateTemplateModal
        voiceMailTemplate={selectedTemplate}
        edit={editing}
        isOpen={createModalOpened}
        onClose={() => createModalHandlers.close()}
        refetchQueries={[GET_VOICEMAIL_TEMPLATES]}
      />
      <DeleteModal
        title={`Are you sure you want to delete the template ${selectedTemplate?.title}?`}
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default VoiceMailTemplateTab
